import { ScheduleStatusPillProps } from 'screens/Schedule/ScheduleStatusPill/Types'
import { Pill } from 'components/Pill/Pill'
import { useMemo } from 'react'
import Styles from './ScheduleStatusPill.module.scss'
import cx from 'classnames'
import { usePreparedSchedule } from 'screens/Schedule/usePreparedSchedule'
import { BrandPlanScheduleContentStatus, SuggestionRequestStatus } from 'types'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

type ScheduleStatus = 'ready' | 'paused' | 'posting' | 'review' | 'approved' | 'posted' | 'generating' | 'failed'

const ScheduleStatusLabel: Record<ScheduleStatus, string> = {
  ready: 'Ready to generate',
  paused: 'Paused',
  posting: 'Posting',
  review: 'Review',
  approved: 'Approved',
  posted: 'Posted',
  generating: 'Generating content',
  failed: 'Generation Failed',
}

export const useScheduleStatus = ({ scheduleId }: { scheduleId: number }): ScheduleStatus => {
  const { data: schedule } = usePreparedSchedule({ scheduleId })
  const unreviewedContentsCount = schedule?.unreviewed_contents_count || 0
  const scheduleStatus = schedule?.scheduled_status
  const sentForApproval = schedule?.sent_for_approval
  const generationStatus = schedule?.status
  const contentCount = schedule?.contents.length || 0

  return useMemo((): ScheduleStatus => {
    const isGenerating =
      !!generationStatus &&
      (
        [SuggestionRequestStatus.Pending, SuggestionRequestStatus.Processing] as BrandPlanScheduleContentStatus[]
      ).includes(generationStatus)

    if (isGenerating && contentCount > 0) {
      return 'generating'
    }

    if (scheduleStatus === 'paused') {
      return 'paused'
    }

    if (scheduleStatus === 'publishing') {
      return 'posting'
    }

    if (scheduleStatus === 'published') {
      return 'posted'
    }

    if (generationStatus === SuggestionRequestStatus.Failed) {
      return 'failed'
    }

    if (unreviewedContentsCount > 0) {
      return 'review'
    }

    if (sentForApproval && unreviewedContentsCount < 1 && generationStatus === SuggestionRequestStatus.Successful) {
      return 'approved'
    }

    if (scheduleStatus === 'planned') {
      return 'ready'
    }

    return 'ready'
  }, [scheduleStatus, unreviewedContentsCount, sentForApproval, contentCount, generationStatus])
}

export const ScheduleStatusPill = ({ scheduleId }: ScheduleStatusPillProps) => {
  const status = useScheduleStatus({ scheduleId })
  const { data: schedule } = usePreparedSchedule({ scheduleId })
  const unreviewedContentsCount = schedule?.unreviewed_contents_count || 0

  const label = useMemo(() => {
    if (status === 'ready' && schedule?.approvalDate) {
      const daysUntil = dayjs.utc(schedule.approvalDate).startOf('day').diff(dayjs.utc().startOf('day'), 'day')

      if (daysUntil <= 5) {
        if (daysUntil < 1) return 'Generating today'
        if (daysUntil < 2) return 'Generating tomorrow'

        return `Generating in ${daysUntil} days`
      }
      return `Generating on ${schedule.approvalDate.format('MMM D')}`
    }

    if (status !== 'review') {
      return ScheduleStatusLabel[status]
    }

    return `${unreviewedContentsCount} to Review`
  }, [status, schedule?.approvalDate, unreviewedContentsCount])

  return (
    <Pill className={cx(Styles.pill, Styles[status])} size="md">
      {label}
    </Pill>
  )
}
